import React, { useState, useEffect } from 'react';
import { Table, Button, Container, Card, Modal, Form } from 'react-bootstrap';
import axios from 'axios';
import Swal from 'sweetalert2';

enum FieldOption {
  YES = 'yes',
  NO = 'no',
}

const FormCustomFields = () => {
  const [fields, setFields] = useState<{
    jabatan_id: string;
    label: string;
    name: string;
    type: string;
    required: boolean;
    values: string;
    use_camera_only: boolean;
  }[]>([]);

  const [jabatanOptions, setJabatanOptions] = useState<{ id: string; name: string }[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [newField, setNewField] = useState({
    jabatan_id: '',
    label: '',
    type: '',
    required: FieldOption.NO, // Use enum
    values: '',
    use_camera_only: FieldOption.NO, // Use enum
  });

  const inputTypes = [
    'text',
    'number',
    'email',
    'password',
    'date',
    'checkbox',
    'radio',
    'file',
    'url',
    'tel',
  ];

  useEffect(() => {
    const fetchJabatanOptions = async () => {
      try {
        const token = localStorage.getItem('authToken'); // Retrieve token from localStorage
        if (!token) {
          throw new Error('Authentication token is missing. Please log in again.');
        }

        Swal.fire({
          title: 'Loading...',
          text: 'Fetching jabatan list',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const response = await axios.get('/master-jabatan', {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (Array.isArray(response.data)) {
          setJabatanOptions(
            response.data.map((item: { id: number; jabatan: string }) => ({
              id: item.id.toString(),
              name: item.jabatan,
            }))
          );
        } else {
          console.error('Unexpected response format:', response.data);
          setJabatanOptions([]);
        }
      } catch (error) {
        console.error('Error fetching jabatan list:', error);
        setJabatanOptions([]);
      } finally {
        Swal.close(); // Close SweetAlert2 loading
      }
    };

    const fetchCustomFields = async () => {
      try {
        const token = localStorage.getItem('authToken'); // Retrieve token from localStorage
        if (!token) {
          throw new Error('Authentication token is missing. Please log in again.');
        }

        Swal.fire({
          title: 'Loading...',
          text: 'Fetching custom fields',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const response = await axios.get('/custom-fields', {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (Array.isArray(response.data)) {
          setFields(
            response.data.map((field: any) => ({
              ...field,
              required: field.required === FieldOption.YES,
              use_camera_only: field.use_camera_only === FieldOption.YES,
              visible: field.visible === 'true', // Convert "visible" to boolean
            }))
          );
        } else {
          console.error('Unexpected response format:', response.data);
          setFields([]);
        }
      } catch (error) {
        console.error('Error fetching custom fields:', error);
        setFields([]);
      } finally {
        Swal.close(); // Close SweetAlert2 loading
      }
    };

    fetchJabatanOptions();
    fetchCustomFields();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    const checked = (e.target as HTMLInputElement).checked;
    setNewField({
      ...newField,
      [name]: type === 'checkbox' ? (checked ? FieldOption.YES : FieldOption.NO) : value, // Use enum
    });
  };

  const handleAddField = async () => {
    const generatedName = newField.label.toLowerCase().replace(/\s+/g, '_');
    const fieldToSubmit = {
      ...newField,
      jabatan_id: newField.jabatan_id || '0', // Default to '0' if not selected
      name: generatedName,
      required: newField.required, // Already "yes" or "no" from FieldOption
      use_camera_only: newField.use_camera_only, // Already "yes" or "no" from FieldOption
    };

    try {
      const token = localStorage.getItem('authToken'); // Retrieve token from localStorage
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      Swal.fire({
        title: 'Saving...',
        text: 'Submitting new field',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await axios.post('/custom-fields', fieldToSubmit, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setFields([
        ...fields,
        {
          ...fieldToSubmit,
          required: fieldToSubmit.required === FieldOption.YES,
          use_camera_only: fieldToSubmit.use_camera_only === FieldOption.YES,
        },
      ]);
      Swal.fire('Success', 'Field added successfully!', 'success');
    } catch (error) {
      console.error('Error submitting new field:', error);
      Swal.fire('Error', 'Failed to add field. Please try again.', 'error');
    } finally {
      setNewField({
        jabatan_id: '',
        label: '',
        type: '',
        required: FieldOption.NO, // Reset to "no"
        values: '',
        use_camera_only: FieldOption.NO, // Reset to "no"
      });
      setShowModal(false);
    }
  };

  return (
    <Container fluid className="py-4">
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0"></h5>
          <Button variant="primary" onClick={() => setShowModal(true)}>
            <i className="ri-add-line"></i> Add Field
          </Button>
        </Card.Header>
        <Card.Body>
          <Table striped bordered hover responsive className="text-center align-middle">
            <thead>
              <tr>
                <th>#</th>
                <th>Jabatan ID</th>
                <th>Label</th>
                <th>Name</th>
                <th>Type</th>
                <th>Required</th>
                <th>Values</th>
                <th>Use Camera Only</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {fields.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center">
                    No fields available.
                  </td>
                </tr>
              ) : (
                fields.map((field, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{jabatanOptions.find((opt) => opt.id === field.jabatan_id)?.name || field.jabatan_id}</td>
                    <td>{field.label}</td>
                    <td>{field.name}</td>
                    <td>{field.type}</td>
                    <td>{field.required ? FieldOption.YES : FieldOption.NO}</td>
                    <td>{field.values}</td>
                    <td>{field.use_camera_only ? FieldOption.YES : FieldOption.NO}</td>
                    <td>
                      <Button variant="outline-primary" size="sm" className="me-2">Edit</Button>
                      <Button variant="outline-danger" size="sm">Delete</Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Field</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Jabatan ID</Form.Label>
              <Form.Select
                name="jabatan_id"
                value={newField.jabatan_id}
                onChange={handleInputChange}
              >
                <option value="">Select Jabatan</option>
                {jabatanOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Label</Form.Label>
              <Form.Control
                type="text"
                name="label"
                value={newField.label}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Type</Form.Label>
              <Form.Select
                name="type"
                value={newField.type}
                onChange={handleInputChange}
              >
                <option value="">Select Type</option>
                {inputTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Mandatory"
                name="required"
                checked={newField.required === FieldOption.YES} // Ensure binding to enum
                onChange={(e) =>
                  setNewField({
                    ...newField,
                    required: e.target.checked ? FieldOption.YES : FieldOption.NO, // Update enum value
                  })
                }
              />
            </Form.Group>
            {['radio', 'checkbox'].includes(newField.type) && (
              <Form.Group className="mb-3">
                <Form.Label>Values</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="values"
                  value={newField.values}
                  onChange={handleInputChange}
                  placeholder="Enter one value per line"
                />
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Use Camera Only"
                name="use_camera_only"
                checked={newField.use_camera_only === FieldOption.YES} // Ensure binding to enum
                onChange={(e) =>
                  setNewField({
                    ...newField,
                    use_camera_only: e.target.checked ? FieldOption.YES : FieldOption.NO, // Update enum value
                  })
                }
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddField}>
            Add Field
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default FormCustomFields;
